import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import API from "../services/api";

const prettyDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const statusVariant = (status) => {
  if (status === "sent") return "success";
  if (status === "failed") return "danger";
  if (status === "skipped") return "secondary";
  return "dark";
};

const AdminWhatsAppHistory = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const loadLogs = async ({ isRefresh = false } = {}) => {
    setError("");
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("limit", "200");
      if (status !== "all") query.set("status", status);
      const res = await API.get(`/admin/whatsapp/logs?${query.toString()}`);
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load WhatsApp history");
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filteredLogs = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((log) => {
      const haystack = [
        log.to,
        log.recipientName,
        log.templateName,
        log.message,
        log.eventType,
        log.sourceModule,
        log.status,
        log.error,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [logs, search]);

  return (
    <div className="container mt-4">
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0">WhatsApp Message History</h5>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => loadLogs({ isRefresh: true })}
              disabled={refreshing || loading}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </div>

          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </Form.Select>
            </Col>
            <Col md={9}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by number, name, template, message, status, error"
              />
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover bordered size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th style={{ minWidth: 170 }}>Sent At</th>
                    <th style={{ minWidth: 130 }}>To</th>
                    <th style={{ minWidth: 120 }}>Recipient</th>
                    <th style={{ minWidth: 130 }}>Template</th>
                    <th style={{ minWidth: 200 }}>Message</th>
                    <th>Status</th>
                    <th style={{ minWidth: 120 }}>Event</th>
                    <th style={{ minWidth: 180 }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-3">No WhatsApp history found</td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => (
                      <tr key={log._id}>
                        <td>{prettyDateTime(log.sentAt || log.createdAt)}</td>
                        <td>{log.to || "—"}</td>
                        <td>{log.recipientName || "—"}</td>
                        <td>{log.templateName || "—"}</td>
                        <td>{log.message || "—"}</td>
                        <td>
                          <Badge bg={statusVariant(log.status)}>{log.status || "unknown"}</Badge>
                        </td>
                        <td>{log.eventType || log.sourceModule || "—"}</td>
                        <td>{log.error || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminWhatsAppHistory;
